import React, { useEffect, useMemo, useState } from 'react';

import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';

import { useStripeCountry } from '../../../config/hooks';
import { useSenderProvider } from '../../../entities/providers/hooks';

import { createStripeIntent } from '../../../client';

import { PayBlock } from './card';

import { PaymentMethodCallbacksProps, PaymentMethodProps } from './payment-method.types';
import { PayPrice, PayPriceCommission } from './styled';

import { CURRENCY, currencyToSign } from '../../../helpers/currency';
import { getStripeCommission } from '../../../helpers/commission';
import { simpleSum } from '../../../helpers/numbers';

import { useTranslation } from '../../../i18n';
import { warning } from '../../../helpers/alert';

export interface PayWithApplepayBtn extends PaymentMethodCallbacksProps {
  currency: CURRENCY;
  amount: number;
  label: string;
}

const PayButton: React.FC<PayWithApplepayBtn> = ({ amount, currency, label, onNotSupported, onPay, onError }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<NotTyped>(null);

  const country = useStripeCountry();
  const provider = useSenderProvider();

  useEffect(() => {
    async function makePayment() {
      if (stripe) {
        const pr = stripe.paymentRequest({
          country,
          currency: String(currency).toLowerCase(),
          total: {
            label,
            amount,
          },
          requestShipping: false,
          requestPayerName: true,
          requestPayerEmail: true,
        });

        pr.on('paymentmethod', async (ev) => {
          try {
            const intent = await createStripeIntent({
              currency,
              amount,
              provider,
            });

            // Confirm the PaymentIntent without handling potential next actions (yet).
            const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
              intent.client_secret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            );

            if (!paymentIntent) {
              ev.complete('fail');

              return;
            }

            if (confirmError) {
              // Report to the browser that the payment failed, prompting it to
              // re-show the payment interface, or show an error message and close
              // the payment interface.
              ev.complete('fail');
            } else {
              // Report to the browser that the confirmation was successful, prompting
              // it to close the browser payment method collection interface.
              ev.complete('success');
              // Check if the PaymentIntent requires any actions and if so let Stripe.js
              // handle the flow. If using an API version older than "2019-02-11" instead
              // instead check for: `paymentIntent.status === "requires_source_action"`.
              if (paymentIntent.status === 'requires_action') {
                // Let Stripe.js handle the rest of the payment flow.
                const { error } = await stripe.confirmCardPayment(intent.client_secret);
                if (error) {
                  // The payment failed -- ask your customer for a new payment method.
                  if (typeof onError === 'function') {
                    onError();
                  }
                } else if (typeof onPay === 'function') {
                  // The payment has succeeded.
                  onPay(paymentIntent);
                }
              } else if (typeof onPay === 'function') {
                // The payment has succeeded.
                onPay(paymentIntent);
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            warning('Stripe confirm payment error');
          }
        });

        // Check the availability of the Payment Request API.
        pr.canMakePayment().then((result: NotTyped) => {
          if (result) {
            setPaymentRequest(pr as NotTyped);
          } else if (typeof onNotSupported === 'function') {
            onNotSupported();
          }
        });
      }
    }

    makePayment().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
      warning('Stripe error');
    });
  }, [stripe, country, provider]);

  if (paymentRequest) {
    return <PaymentRequestButtonElement options={{ paymentRequest }} />;
  }

  // Use a traditional checkout form.
  return null;
};

export const PayWithApplePay: React.FC<PaymentMethodProps> = ({
  price,
  currency,
  orderID,
  onPay,
  onError,
  onNotSupported,
}) => {
  const [t] = useTranslation('rates');

  const [isSupported, setSupported] = useState(true);

  const sign = currencyToSign(currency);
  const commission = getStripeCommission(Number(price));

  const fullPrice = useMemo(() => Math.floor(simpleSum([price, commission]) * 100), [commission, price]);

  if (!isSupported) {
    return null;
  }

  return (
    <PayBlock className="is-hover-shadow">
      <PayPrice>
        {price} {sign}
      </PayPrice>
      <PayPriceCommission>
        (+{t('rates.payments.commission')} {commission} {sign})
      </PayPriceCommission>

      <PayButton
        amount={fullPrice}
        currency={currency}
        label={`Pay for the "${orderID}"`}
        onPay={onPay}
        onError={onError}
        onNotSupported={() => {
          setSupported(false);

          if (typeof onNotSupported === 'function') {
            onNotSupported();
          }
        }}
      />
    </PayBlock>
  );
};
